/**
 * How a document is rendered follows from its filename, which is the only thing
 * the viewer is given. The backend names the same formats in
 * internal/types/extension.go, and the two lists are meant to stay in step.
 */
export type DocumentKind =
  | 'pdf'
  | 'spreadsheet'
  | 'csv'
  | 'xml'
  | 'image'
  | 'text'
  | 'other'

const KINDS: Record<string, DocumentKind> = {
  pdf: 'pdf',

  // Everything the workbook engine reads: the Office Open XML formats, the
  // binary XLSB, and the old BIFF .xls, which earlier could only be offered as
  // a download.
  xlsx: 'spreadsheet',
  xlsm: 'spreadsheet',
  xltx: 'spreadsheet',
  xltm: 'spreadsheet',
  xlam: 'spreadsheet',
  xlsb: 'spreadsheet',
  xls: 'spreadsheet',
  xlt: 'spreadsheet',
  xla: 'spreadsheet',

  csv: 'csv',
  tsv: 'csv',

  xml: 'xml',

  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
  bmp: 'image',

  txt: 'text',
  log: 'text',
  json: 'text',
}

/** The extension without its dot, lowercased, or '' when the name has none. */
export function fileExtension(name: string): string {
  const lastDot = name.lastIndexOf('.')
  if (lastDot < 0 || lastDot === name.length - 1) return ''

  const extension = name.slice(lastDot + 1).toLowerCase()

  // An email subject stands in for a filename in places, and "Order 123 of 4.2"
  // has an extension only in the sense that a dot appears in it.
  if (extension.length > 5 || /[^a-z0-9]/.test(extension)) return ''

  return extension
}

export function documentKindFromName(name: string): DocumentKind {
  return KINDS[fileExtension(name)] ?? 'other'
}

/** What the document is called in the one line shown above a preview. */
export function documentKindLabel(name: string): string {
  const extension = fileExtension(name)
  if (!extension) return 'File'

  return extension.toUpperCase()
}
